import { useGomakeTheme } from "@/hooks/use-gomake-thme";
import { useIsMobile } from "@/hooks/use-responsive";
import { convertWidthToVW } from "@/utils/adapter";
import { FONT_FAMILY } from "@/utils/font-family";
import { useMemo } from "react";

const useStyle = ({
  insideStyle,
  contentStyle,
  headerPadding,
  headerPaddingTop,
  isMiddleTitle,
  modalTitleColor,
  closeIconStyle,
  hasModalDescription,
}: any) => {
  const { primaryColor, errorColor, neutralColor } = useGomakeTheme();
  const isMobile = useIsMobile();
  const classes = useMemo(() => {
    const headerPaddingValue = hasModalDescription ? 40 : headerPadding;
    // Callers size modals with desktop percentages (25%-50%), which leaves them
    // unusably narrow on a phone. Applied after insideStyle so it wins.
    const mobileWidthOverride = isMobile
      ? {
          width: "94%",
          maxWidth: "94%",
          maxHeight: "90%",
        }
      : {};
    return {
      container: {
        display: "flex",
        flexDirection: "column" as "column",
        justifyContent: "flex-start",
        alignItems: "flex-start",
        width: "45%",
        borderRadius: 16,
        backgroundColor: "#FFFFFF",
        position: "absolute" as "absolute",
        left: "50%",
        top: "50%",
        transform: "translate(-50%, -50%)",
        height: "90%",
        overFlow: "auto" as "auto",
        paddingTop: convertWidthToVW(25),
        paddingLeft: convertWidthToVW(20),
        paddingRight: convertWidthToVW(20),
        paddingBottom: convertWidthToVW(20),
        ...insideStyle,
        ...mobileWidthOverride,
        ...(isMobile && {
          paddingTop: 16,
          paddingLeft: 16,
          paddingRight: 16,
          paddingBottom: 16,
        }),
      },

      closeIcon: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        cursor: "pointer" as "pointer",
        ...(hasModalDescription && {
          position: "absolute" as "absolute",
          right: 12,
          top: 12,
          width: 32,
          minWidth: 32,
          height: 32,
          minHeight: 32,
          borderRadius: 8,
        }),
        ...closeIconStyle,
      },

      titleModalStyle: {
        display: "flex",
        color:
          modalTitleColor ||
          `var(--theme-typography-title-color, ${primaryColor(500)})`,
        ...FONT_FAMILY.Lexend(500, 24),
        ...(isMiddleTitle && { margin: "0 auto" }),
        ...(hasModalDescription && {
          fontFamily: "var(--theme-typography-title-font-family, inherit)",
          fontWeight: 500,
          fontSize: 32,
          lineHeight: "32px",
          color: "#1A1A1A",
        }),
      },
      headerDescriptionStyle: {
        fontFamily: "var(--theme-typography-body-font-family, inherit)",
        fontWeight: 500,
        fontSize: 20,
        lineHeight: "24px",
        color: "#636363",
      },
      titleBlockModalStyle: {
        display: "flex",
        color: errorColor(500),
        ...FONT_FAMILY.Lexend(500, 24),
      },
      boxContainer: {
        height: "100%",
        maxHeight: "100%",
        width: "100%",
        overflowY: "auto" as "auto",
        ...contentStyle,
      },
      childrenContainer: {
        display: "flex",
        flexDirection: "column" as "column",
        width: "100%",
      },
      headerContainer: {
        width: "100%",
        display: "flex",
        justifyContent: "space-between",
        alignItems: hasModalDescription ? "flex-start" : "baseline",
        paddingRight: headerPaddingValue,
        paddingLeft: headerPaddingValue,
        paddingTop: hasModalDescription ? 40 : headerPaddingTop,
        flexShrink: 0,
        ...(hasModalDescription && { position: "relative" as "relative" }),
        ...(headerPaddingTop != null && !hasModalDescription && { paddingTop: headerPaddingTop }),
      },
      headerTitleWrapper: {
        display: "flex",
        flexDirection: "column" as "column",
        alignItems: "flex-start",
        gap: 20,
        flex: 1,
      },
      modalContainer: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        width: isMobile ? "90%" : "25%",
        borderRadius: 20,
        backgroundColor: "#FFFFFF",
        position: "absolute" as "absolute",
        left: "50%",
        top: "50%",
        transform: "translate(-50%, -50%)",
        boxShadow: "0px 10px 30px rgba(0, 0, 0, 0.05)",
      },
      content: {
        display: "flex",
        flexDirection: "column" as "column",
        justifyContent: "center",
        alignItems: "center",
        padding: 30,
        width: "80%",
      },
      icon: {
        display: "flex",
        flexDirection: "column" as "column",
        marginBottom: 10,
      },
      iconStyle: {
        width: "61",
        height: "62",
        color: "#a1a2cd",
      },
      title: {
        ...FONT_FAMILY.Lexend(700, 16),
        color: primaryColor(500),
        marginBottom: 14,
        textAlign: "center" as "center",
      },
      subTitle: {
        ...FONT_FAMILY.Lexend(400, 14),
        color: neutralColor(700),
        textAlign: "center" as "center",
        marginBottom: 33,
        width: "89%",
      },
      btnsContainer: {
        display: "flex",
        flexDirection: "row" as "row",
        justifyContent: "center",
        alignItems: "center",
        gap: 11,
        width: "100%",
      },
      confermBtn: {
        width: "50%",
        height: 33.29,
        borderRadius: "10px",
        ...FONT_FAMILY.Lexend(500, 14),
        backgroundColor: primaryColor(500),
      },
      cancelBtn: {
        width: "50%",
        height: 33.29,
        borderRadius: "10px",
        ...FONT_FAMILY.Lexend(500, 14),
      },
    };
  }, [insideStyle, contentStyle, headerPadding, headerPaddingTop, closeIconStyle, hasModalDescription, isMobile]);
  return {
    classes,
  };
};
export { useStyle };
